/**
 * DECLARED FORMAT CHECKS — `pattern` / `patternMessage`, and URL normalization.
 *
 * Two things a block author could declare that the app ignored until 2026-08-24:
 *
 *  1. `pattern` was parsed and carried but never tested against anything (monolith
 *     parity — the old CloudPage did nothing with it either). Layne authored the
 *     first real one on 2026-08-24 and reasonably asked why nothing happened.
 *  2. Nothing anywhere put a protocol on a URL. A requester typing `www.myurl.com`
 *     shipped `href="www.myurl.com"`, which a browser reads as a RELATIVE path, so
 *     the CTA went to lennar.com/…/www.myurl.com. This is the bug a division
 *     reported to Layne; her fix attempt was `"default": "https://"`, which cannot
 *     work because the value is typed OVER the default.
 *
 * WARN, NEVER BLOCK — the load-bearing decision here. A pattern is authored in
 * Content Builder with no way to test it against real copy, so it WILL sometimes be
 * too strict. Layne's first one rejected `$5,000 toward your closing costs`, a
 * curly-quoted headline pasted out of Word, and any two-line headline on a field
 * that declares `lines: 2`. If a pattern could block a submit, that regex would
 * have stopped real sends. A hint someone can overrule is the right severity for a
 * rule nobody can unit-test.
 *
 * NORMALIZE, DON'T SCOLD, for the one case with exactly one right answer: a URL
 * missing its scheme. Telling someone "add https://" is a worse product than adding
 * it. So `type: "url"` gets fixed on commit rather than flagged.
 */
import type { GuardrailField } from "./guardrails";

/** the generic line when a pattern arrives with no `patternMessage` */
export const PATTERN_FALLBACK = "This doesn't match the format this field expects";

/**
 * A bare domain — something with a dot and no scheme, which is almost certainly a
 * URL the requester expected to work.
 *
 * Deliberately NOT "anything without a scheme": `mailto:`, `tel:`, an AMPscript
 * block, and a `%%` merge token must all pass through untouched, and so must a
 * root-relative `/new-homes/florida` (a real, valid link on lennar.com). Requiring
 * a dot before the first slash is what separates `www.myurl.com/x` from `/x`.
 */
const BARE_DOMAIN = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+(:\d+)?(?=$|[/?#])/i;

/** already carries a scheme — http:, https:, mailto:, tel:, ftp:, anything */
const HAS_SCHEME = /^[a-z][a-z0-9+.-]*:/i;

/** an AMPscript / merge-field value the app must never touch */
const DYNAMIC = /^(%%|\{\{|<)/;

/**
 * Put `https://` on a bare domain. Returns the input unchanged for everything else,
 * so this is safe to run on every commit of every url field.
 *
 * `//example.com` (protocol-relative) is also completed, because an email client
 * has no page protocol to inherit — it is a broken link in an email even though it
 * works in a browser.
 */
export function normalizeUrl(raw: unknown): string {
  const v = String(raw ?? "").trim();
  if (!v) return v;
  if (DYNAMIC.test(v)) return v;
  if (v.startsWith("//")) return "https:" + v;
  if (HAS_SCHEME.test(v)) return v;
  if (v.startsWith("/") || v.startsWith("#") || v.startsWith("?")) return v;  /* relative, intentional */
  if (BARE_DOMAIN.test(v)) return "https://" + v;
  return v;                                  /* not recognisably a URL — leave it alone */
}

/**
 * ── EVERY URL FIELD STARTS AT `https://` (2026-08-27) ─────────────────────────────
 *
 * Jack: "i'd like to hard code https:// into every url."
 *
 * A url box that opens EMPTY invites `www.myurl.com` and `lennar.com/x`, which a
 * browser reads as a relative path — the exact division report that produced
 * `normalizeUrl` in the first place. Normalising on blur fixes it after the fact; this
 * removes the chance to make the mistake, because the scheme is already in the box and
 * the requester types after it.
 *
 * SHOWN, NOT WRITTEN. This is the value the CONTROL displays; nothing is committed to
 * the block until a keystroke, so a field nobody touches still reads as empty to the
 * required gate (`urlBlank` treats a bare "https://" as blank on purpose — a dead
 * button ships a broken email and must still be flagged at Done).
 *
 * Left alone: a value that already carries any scheme (`mailto:`, `tel:`, `https:`), an
 * AMPscript or merge token, and a root-relative path — the same exemptions
 * `normalizeUrl` makes, for the same reason. A field with a real value is untouched.
 */
export const URL_PREFIX = "https://";
export function urlStart(v: unknown): string {
  const t = String(v ?? "");
  if (t.trim() !== "") return t;
  return URL_PREFIX;
}

/**
 * ── THE SCHEME AS A LOCKED PREFIX, NOT AS TEXT (2026-08-27) ───────────────────────
 *
 * Jack, on the box opening at "https://": "can you lock this in place? like so you cant
 * even delete it? and it's just a faded text."
 *
 * Prefilling the value was better than an empty box and still wrong in one way: the
 * scheme was ordinary text, so a backspace at the caret's home position deleted part of
 * it and left `https:/` — which is the same broken href with an extra step. So the
 * scheme moves OUT of the editable value: the control renders a faded, unselectable
 * `https://` and the input holds only what comes after it.
 *
 * `urlSplit` decides whether a value can be shown that way. It CANNOT when the value is
 * not an https URL — `mailto:`, `tel:`, `http://`, a root-relative path, an AMPscript or
 * merge token. Those get the plain full-width box, because hiding a prefix that isn't
 * there would make the real value unreadable and uneditable. Same exemption list as
 * `normalizeUrl`, for the same reason.
 *
 * `urlJoin` puts it back, and strips a scheme the requester pasted INTO the rest — a
 * paste of a full URL is the common case and must not produce `https://https://…`.
 */
const HTTPS = "https://";

export function urlSplit(v: unknown): { affix: boolean; rest: string } {
  const t = String(v ?? "");
  if (t === "") return { affix: true, rest: "" };
  if (t.toLowerCase().indexOf(HTTPS) === 0) return { affix: true, rest: t.slice(HTTPS.length) };
  return { affix: false, rest: t };
}

export function urlJoin(rest: unknown): string {
  /* a pasted scheme is dropped rather than doubled; a pasted `http://` is dropped too —
     the affix says https and the value must agree with what the requester can see */
  /* ── TRIM, AND TRIM ON BOTH SIDES OF THE SCHEME STRIP (2026-09-01) ───────────────
     Layne asked for whitespace to be trimmed "before validating". Validating is too
     late, and trimming there would not have fixed this: the OLD `^\s*https?:\/*` ate a
     LEADING space only when a scheme followed it, so a paste of " www.lennar.com/x "
     was stored as `https:// www.lennar.com/x ` — a space between the scheme and the
     host, which is a dead button, not a formatting nit.
     And her own rule cannot catch it. Measured: `checkPattern("https:// www.lennar.com",
     "^https://.+")` returns ok — `.+` matches the space happily. So this had to be fixed
     where the value is BUILT; no amount of pattern enforcement would have found it.
     Trimmed twice on purpose: once before the scheme strip (so ` https://x` loses its
     leading space) and once after (so `https:// x` loses the space the strip exposed). */
  /* ── ONLY A WHOLE SCHEME IS STRIPPED (2026-09-08, pre-UAT e2e A8) ────────────────
     The strip was `^https?:\/*`, which also matched the PARTIAL scheme a requester is
     mid-way through TYPING: at "https:" the rest became "", the box emptied, and the
     next keystroke ("/") was joined as `https:///` — which the guardrail then refused.
     Typing a full URL into a box that already shows the scheme is the common case, so
     the scheme is stripped only once it is complete (`https://`), and any slashes left
     at the front of the rest are dropped — nothing valid follows `https://` with a
     slash. A pasted `http://` still collapses to the https the affix shows. */
  const r = String(rest ?? "").trim().replace(/^https?:\/\//i, "").replace(/^\/+/, "").trim();
  return r === "" ? "" : HTTPS + r;
}

/** true when normalizeUrl would change this value — used to explain the fix */
export const urlNeedsScheme = (raw: unknown): boolean => {
  const v = String(raw ?? "").trim();
  return !!v && normalizeUrl(v) !== v;
};

/**
 * Compile a declared pattern.
 *
 * ANCHORED AND MULTILINE-SAFE. Two corrections applied to whatever the author
 * wrote, because both bit Layne's first pattern:
 *
 *  - the `s` (dotAll) flag, so `.` matches a newline. Without it a pattern ending
 *    `.*$` fails on a two-line value — and `lines: 2` fields are common. An author
 *    writing `.*` means "the rest of it", not "the rest of it unless they pressed
 *    Enter".
 *  - a bad regex returns null rather than throwing, so a typo in Content Builder
 *    degrades to "no check" instead of breaking the panel for every requester.
 */
export function compilePattern(pattern?: string): RegExp | null {
  const p = String(pattern ?? "").trim();
  if (!p) return null;
  try {
    return new RegExp(p, "s");
  } catch {
    return null;                             /* unparseable — behave as if undeclared */
  }
}

export interface PatternResult {
  /** false only when a pattern is declared, parses, and the value fails it */
  ok: boolean;
  /** the author's `patternMessage`, or the generic fallback */
  message: string;
}

const PASS: PatternResult = { ok: true, message: "" };

/**
 * Test a value against its field's declared pattern.
 *
 * TRIMMED FIRST. A leading space is not a format error the requester should be
 * lectured about — Layne's pattern rejected `" Enjoy all the benefits"` purely for
 * the space, which would read as a nonsense complaint.
 *
 * An EMPTY value always passes: emptiness is what `required` is for (and the
 * Studio's required-fields check already owns it). Failing a blank optional
 * paragraph against a sentence-case rule would flag three of the four paragraphs on
 * Layne's body block the moment it loads.
 */
export function checkPattern(value: unknown, rule?: GuardrailField | null): PatternResult {
  if (!rule) return PASS;
  const re = compilePattern(rule.pattern);
  if (!re) return PASS;
  const v = String(value ?? "").trim();
  if (!v) return PASS;
  if (re.test(v)) return PASS;
  return { ok: false, message: String(rule.patternMessage || "").trim() || PATTERN_FALLBACK };
}

/**
 * ── THE RANGE FALLBACK WAS ADDED AND REMOVED THE SAME DAY (2026-09-01) ─────────────
 *
 * For a few hours `checkPattern` accepted a dash-joined pair whose halves each passed
 * the rule, so a Featured card's `3-4` beds or `2,100–2,400` sq ft would not be flagged
 * against `^\d{1,2}$`. Layne's answer was better than the fallback: she widened the
 * two Featured-block patterns HERSELF (the rules are hers, and now they say what they
 * mean), and asked that listing-table rows keep flagging a range — because each row is a
 * specific homesite, and a range there is wrong data, not a format quirk.
 *
 * So the check is strict again: a value passes its pattern or it does not. Kept as a
 * note rather than deleted so the next person who reaches for a range fallback finds
 * out why it was declined, and by whom. `patternRange.test.ts` pins the strict
 * behaviour and the formatter-to-pattern pairing that makes it safe.
 */
